import { useEffect, useState } from "react";
import AuthLayout from "../../layouts/AuthLayout"
import MobileView from "../../layouts/MobileView/MobileView"
import { Link, useNavigate } from "react-router-dom";
import { INIT_IG_AUTH_ROUTE, LANDING_PAGE_URL } from "../../constants/constants";
import VerifyOTP from "../../components/login/VerifyOTP";
import { useDispatch } from "react-redux";
import { AppDispatch, getIntendedPath, RootState } from "../../redux-stores/store";
import { useSelector } from "react-redux";
import { fetchInfluencer } from "../../redux-stores/slices/influencerSlice";
import { login } from "../../redux-stores/slices/authSlice";
import { generateOTP, registerUser } from "../../services/api/auth";
import { analyticsSetUser, analyticsLog } from "../../lib/analytics";
import { toast } from "react-toastify";
import { getErrorMessage, isValidMobileNumber } from "../../utils/helpers";

interface ValidationResults {
    name?: null | { status: boolean, message?: string },
    number?: null | { status: boolean, message?: string }
}

const Register: React.FC = () => {
    const [number, setNumber] = useState('');
    const [name, setName] = useState<string>('');
    const [otpSent, setOTPSent] = useState<boolean>(false);
    const [mode, setMode] = useState<string>('');

    const [validating, setValidating] = useState<boolean>(false);

    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    const connectionRequired = useSelector((state: RootState) => state.influencer.connectionRequired);
    const igAuthWall = useSelector((state: RootState) => state.auth.configurations?.ig_auth_wall === 'y')
    const isVerified = useSelector((state: RootState) => state.influencer.influencer?.verification?.is_verified);

    const [validationResults, setValidationResults] = useState<ValidationResults>({});

    const fetchUser = async (token: string) => {
        await dispatch(fetchInfluencer(token));
        const data = (await dispatch(login(token)).unwrap()).userData;
        return data;
    }

    useEffect(() => {
        if (isAuthenticated) {
            const redirectURL = (connectionRequired && igAuthWall && !isVerified) ? INIT_IG_AUTH_ROUTE : getIntendedPath();
            navigate(redirectURL);
        }
    }, [isAuthenticated, connectionRequired, navigate])

    const validate = () => {
        validateField("name", name);
        validateField("number", number);
    
        return (
            name.length >= 2 &&
            isValidMobileNumber(number)
        );
    };

    const validateField = (field: "name" | "number", value: string) => {
        setValidationResults(prev => {
            const updated = { ...prev };
    
            if (field === "name") {
                if (!value) {
                    updated.name = { status: false, message: "Please enter a valid name" };
                } else if (value.length < 2) {
                    updated.name = { status: false, message: "Name should contain minimum 2 characters" };
                } else {
                    delete updated.name;
                }
            }
    
            if (field === "number") {
                if (!isValidMobileNumber(value)) {
                    updated.number = { status: false, message: "Please enter a valid mobile number" };
                } else {
                    delete updated.number;
                }
            }
    
            return updated;
        });
    };

    const sendOTP = async () => {
        if (!validate()) return false;

        try {
            setValidating(true);
            const res = await generateOTP({ mode: 'mobile', username: number });
            if (res.status) {
                setOTPSent(true);
                setMode(res.mode!);
            }
        } catch (error) {
            console.error(error)
            toast.error(getErrorMessage(error));
        } finally {
            setValidating(false);
        }
    }

    const register = async (data: { username: string, otp: string }) => {
        try {
            let referrer = null;
            if (window.localStorage.getItem("_tt_referrer")) {
                referrer = window.localStorage.getItem("_tt_referrer");
            }
            const result = await registerUser({ ...data, name, referrer });
            if (!result.token) throw new Error("Something went wrong");
            const userData = await fetchUser(result.token)
            if (userData && result.is_new) {
                await analyticsSetUser(userData.user.uuid);
                analyticsLog('sign_up', {
                    name: userData.user.name,
                    uuid: userData.user.uuid,
                })
                navigate('/registration-success');
            }
        } catch (error) {
            console.error(error)
            toast.error(getErrorMessage(error));
        }
    }


    return (
        <MobileView>
            <AuthLayout>
                {
                    !otpSent ? (
                        <div className="position-relative h-100 auth-content-inner">
                            <h4 className="pop w-100 text-center auth-heading">Create Your Account</h4>
                            <div className="auth-form">
                                <div>
                                    <input type="text"
                                        className="form-control validate-name auth-form-input"
                                        id="form1"
                                        placeholder="Enter Your Name"
                                        value={name}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setName(value);
                                            validateField("name", value);
                                        }}
                                    />
                                    {
                                        !!validationResults.name?.message &&
                                        <span className="text-danger">{validationResults.name.message}</span>
                                    }
                                </div>
                                <div>
                                    <div className="auth-form-input-wrapper">
                                        <span>+91</span>
                                        <input type="tel"
                                            className="w-100"
                                            id="form1" placeholder="Enter WhatsApp Number"
                                            value={number}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setNumber(value);
                                                validateField("number", value);
                                            }}
                                        />
                                    </div>
                                    {
                                        !!validationResults.number?.message &&
                                        <span className="text-danger">{validationResults.number.message}</span>
                                    }
                                </div>

                                <button
                                    onClick={sendOTP}
                                    disabled={
                                        validating ||
                                        !name ||
                                        !number ||
                                        Object.keys(validationResults).length > 0
                                    } 
                                    className="new-ui-btn">Continue</button>
                            </div>
                            <div className="d-flex flex-column justify-content-between h-100 gap-5">
                                <div className="auth-nav-content">
                                    <span>Already have an account? <Link to={{ pathname: '/login' }}> Log In </Link></span>
                                </div>
                                <div className="text-center">
                                    <span>
                                        By continuing, you agree to our <a href={LANDING_PAGE_URL + '/legal/privacy.html'} target="_blank" rel="noreferrer"> Privacy Policy </a> and <a href={LANDING_PAGE_URL + '/legal/terms.html'} target="_blank" rel="noreferrer">Terms of Use</a>
                                    </span>
                                </div>
                            </div>

                        </div>
                    ) : (
                        <div className="position-relative h-100">
                            <VerifyOTP mode={mode} number={number} verifyOTP={register} onBackButtonClick={() => setOTPSent(false)} />
                        </div>
                    )
                }
            </AuthLayout>
        </MobileView>

    )
}

export default Register